import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function MessagesPanel({ onUnreadChange }) {
    const [messages, setMessages] = useState(null);

    const load = () =>
        api.get("/admin/messages")
            .then((r) => {
                setMessages(r.data);
                onUnreadChange(r.data.filter((m) => !m.read).length);
            })
            .catch(() => setMessages([]));

    useEffect(() => {
        load();
    }, []);

    const markRead = async (m) => {
        try {
            await api.patch(`/admin/messages/${m.id}`, { read: true });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const del = async (m) => {
        try {
            await api.delete(`/admin/messages/${m.id}`);
            toast.success("Message deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div>
            <h2 className="font-display font-extrabold uppercase text-2xl tracking-tight" data-testid="admin-messages-count">
                Messages
            </h2>
            {!messages ? (
                <p className="py-16 flex justify-center">
                    <Loader2 className="animate-spin" />
                </p>
            ) : messages.length === 0 ? (
                <p
                    className="py-16 text-xs uppercase tracking-[0.18em] text-inkdim"
                    data-testid="admin-messages-empty"
                >
                    No enquiries yet — they'll land here when someone writes in.
                </p>
            ) : (
                <div className="mt-8 space-y-4">
                    {messages.map((m) => (
                        <div
                            key={m.id}
                            data-testid={`admin-message-${m.id}`}
                            className={`border p-5 ${m.read ? "border-line" : "border-ink/40 bg-surface/70"}`}
                        >
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <p className="font-bold">
                                        {m.name} <span className="text-inkdim font-normal text-sm">· {m.email}</span>
                                    </p>
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-inkdim mt-1">
                                        {m.project_type || "General enquiry"} · {new Date(m.created_at).toLocaleString()}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {!m.read && (
                                        <Button
                                            data-testid={`admin-message-read-${m.id}`}
                                            variant="outline"
                                            onClick={() => markRead(m)}
                                            className="rounded-none border-ink/30 h-8 text-xs"
                                        >
                                            <Check size={13} /> Mark read
                                        </Button>
                                    )}
                                    <Button
                                        data-testid={`admin-message-delete-${m.id}`}
                                        variant="outline"
                                        onClick={() => del(m)}
                                        className="rounded-none border-ink/30 h-8 text-xs text-red-700"
                                    >
                                        <Trash2 size={13} />
                                    </Button>
                                </div>
                            </div>
                            <p className="mt-3 text-sm leading-relaxed text-inkdim">{m.message}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
